<script setup>
import { useCalendarBus } from '@/schedule/composables/useCalendarBus'
import { useProfilesQuery } from '@/account/composables/useProfilesQuery'

const { event } = defineProps({
  event: {
    type: Object,
    required: true,
  },
})
const emit = defineEmits(['update', 'close', 'redirectToLogin'])
const calendarBus = useCalendarBus()
const { features } = useFeatures()
const { hasCustomerId, currentCustomerId } = useSessionData()
const api = useApi()
const selectedCustomer = useSelectedCustomer()
const loadingEligibility = ref(false)
const eligibility = ref(undefined)

const isAppointmentType = computed(() => event?.is_appointment)
const { data: profilesData } = useProfilesQuery()
async function fetchRegistrationEligibility() {
  loadingEligibility.value = true
  // if (!event || isAppointmentType.value || !event.type)
  //   return

  const targetTypes = {
    course: 'checkin',
    class: 'class',
    appointment: 'appointment_slot',
  }

  const data = {
    customer: selectedCustomer.value,
    ...(
      event?.type !== 'appointment'
        ? { event: event?.id }
        : { appointment_slot: event?.id }
    ),
    target_type: targetTypes[event?.type],
  }

  eligibility.value = await api.checkRegistrationEligibility({ data })
  loadingEligibility.value = false
}

const selectedCustomerAge = computed(() => {
  return eligibility.value?.global_restrictions.find(restriction => restriction.code === 'age_restricted')?.data?.age
})

const isAgeRestricted = computed(() => {
  return eligibility.value?.global_restrictions.some(restriction => restriction.code === 'age_restricted')
})

const handlers = {
  update: (value = undefined) => onUpdate(value),
  close: () => {
    calendarBus.emit('refresh')
    emit('close')
  },
  redirectToLogin: () => emit('redirectToLogin'),
}

watch(
  () => [event, selectedCustomer.value],
  () => {
    emit('update')
  },
  { deep: true },
)

onMounted(() => {
  if (hasCustomerId && !isAppointmentType.value && event?.type)
    fetchRegistrationEligibility()
})

const isParticipatingCoManager = computed(() => profilesData.value?.some(profile =>
  profile.id !== currentCustomerId
  && profile.id === selectedCustomer.value
  && profile.participating === true
  && profile.family_role === 'manager',
))

const steps = computed(() => {
  const globalRestrictions = eligibility.value?.global_restrictions
  const unrestrictedMembershipOptions = eligibility.value?.membership_options.filter(option => !option.restrictions.length) || []
  const hasMissingPrerequisites = !!eligibility.value?.prerequisites?.completion_url

  return [
    {
      id: 'not_authenticated',
      condition: () => !hasCustomerId,
    },
    {
      id: 'registrations_blocked',
      condition: () =>
        globalRestrictions?.some(
          restriction => restriction.code === 'customer_registration_block',
        ),
    },
    {
      id: 'is_appointment',
      condition: () =>
        isAppointmentType.value,
    },
    {
      id: 'registered',
      condition: () =>
        globalRestrictions?.some(
          restriction => restriction.code === 'already_attending' || restriction.code === 'appointment_slot_unavailable',
        ),
    },
    {
      id: 'registrations_closed',
      condition: () =>
        event?.isEnded || event?.registrationsClosed || globalRestrictions?.some(
          restriction => restriction.code === 'registrations_closed',
        ),
    },
    {
      id: 'age_restriction',
      condition: () => isAgeRestricted.value && features.age_restrictions,
    },
    {
      id: 'prerequisites_incomplete_co_manager',
      condition: () => hasMissingPrerequisites && isParticipatingCoManager.value,
    },
    {
      id: 'prerequisites_incomplete',
      condition: () => hasMissingPrerequisites,
    },
    {
      id: 'event_full',
      condition: () =>
        globalRestrictions?.some(
          restriction => restriction.code === 'event_full',
        ),
    },
    {
      id: 'registration_limit_reached',
      condition: () =>
        globalRestrictions?.some(
          restriction =>
            restriction.code === 'maximum_daily_registrations'
            || restriction.code === 'maximum_weekly_registrations',
        ),
    },
    {
      id: 'registrations_not_open',
      condition: () =>
        globalRestrictions?.some(
          restriction => restriction.code === 'registrations_not_open',
        ),
    },
    {
      id: 'purchase_required',
      condition: () =>
        (eligibility.value?.membership_purchases_available === true && !unrestrictedMembershipOptions.length) || (!unrestrictedMembershipOptions.length && eligibility.value?.drop_in_price?.decimal > 0),
    },
    {
      id: 'contact_us',
      condition: () =>
        !eligibility.value?.drop_in_price && eligibility.value?.membership_purchases_available === false && !unrestrictedMembershipOptions.length,
    },
    {
      id: 'registration_allowed',
      condition: () => true,
    },
  ]
})

const currentStep = computed(() => {
  const step = steps.value.find(step => step.condition() === true)
  return step && step.id
})

function onUpdate(slotDetail) {
  if (isAppointmentType.value)
    emit('close')

  fetchRegistrationEligibility()
  calendarBus.emit('refresh')

  if (slotDetail)
    emit('update', slotDetail.registeredEventId)
  else
    emit('update')
}
</script>

<template>
  <DSLoadingWrapper :loading="loadingEligibility">
    <div
      class="booking__section h-100% w-full flex flex-col gap-2 b-1 border-surface-600 rounded-4px bg-surface-100 p-4 text-sm sm:overflow-y-auto"
    >
      <SelectFamilyMemberDropdown v-if="currentStep !== 'not_authenticated' || currentStep !== 'is_appointment'" />
      <BookingModalNotAuthenticated
        v-if="currentStep === 'not_authenticated'"
        :event="event"
        v-on="handlers"
      />
      <BookingModalRegistrationBlockedStep
        v-else-if="currentStep === 'registrations_blocked'"
        :event="event"
        :eligibility="eligibility"
        v-on="handlers"
      />
      <BookingModalAppointment
        v-else-if="currentStep === 'is_appointment'"
        :event="event"
        :eligibility="eligibility"
        v-on="handlers"
      />
      <BookingModalRegisteredStep
        v-if="currentStep === 'registered'"
        :event="event"
        :eligibility="eligibility"
        v-on="handlers"
      />
      <BookingModalPrerequisitesIncompleteParticipatingCoManagerStep
        v-else-if="currentStep === 'prerequisites_incomplete_co_manager'"
        :event="event"
        :eligibility="eligibility"
        v-on="handlers"
      />
      <BookingModalPrerequisitesIncompleteStep
        v-else-if="currentStep === 'prerequisites_incomplete'"
        :event="event"
        :eligibility="eligibility"
        v-on="handlers"
      />
      <BookingModalEventFullStep
        v-if="currentStep === 'event_full'"
        :event="event"
        :eligibility="eligibility"
        v-on="handlers"
      />
      <BookingModalRegistrationsClosedStep
        v-else-if="currentStep === 'registrations_closed'"
        :event="event"
        :eligibility="eligibility"
        v-on="handlers"
      />
      <BookingModalRegistrationLimitReachedStep
        v-else-if="currentStep === 'registration_limit_reached'"
        :event="event"
        :eligibility="eligibility"
        v-on="handlers"
      />
      <BookingModalRegistrationsNotOpenStep
        v-else-if="currentStep === 'registrations_not_open'"
        :event="event"
        :eligibility="eligibility"
        v-on="handlers"
      />
      <BookingModalAgeRestrictedStep
        v-if="currentStep === 'age_restriction' && features.age_restrictions"
        :event="event"
        :selected-customer="selectedCustomer"
        :selected-customer-age="selectedCustomerAge"
        v-on="handlers"
      />
      <BookingModalPurchaseRequiredStep
        v-else-if="currentStep === 'purchase_required'"
        :event="event"
        :eligibility="eligibility"
        v-on="handlers"
      />
      <BookingModalContactUsStep
        v-else-if="currentStep === 'contact_us'"
        :event="event"
        :eligibility="eligibility"
        v-on="handlers"
      />
      <BookingModalRegistrationsAllowedStep
        v-else-if="currentStep === 'registration_allowed'"
        :event="event"
        :eligibility="eligibility"
        v-on="handlers"
      />
    </div>
  </DSLoadingWrapper>
</template>

<style scoped lang="scss">
:deep([data-ds-component='tooltip'] > button) {
  @apply min-w-full;
}
[data-ds-component='button'] {
  @apply uppercase;
}
</style>
